import {
  RxError,
  type MangoQuery,
  type MangoQuerySelector,
  type MangoQuerySelectorAndIndex,
  type RxCollection,
} from 'rxdb';
import { Observable } from 'rxjs';
import {
  bulkInsertSynced,
  bulkUpsertSynced,
  insertSynced,
  patchSynced,
  upsertSynced,
} from './rxDbOperations';
import {
  type CollectionName,
  type DocOf,
  type RxDbActionRunner,
  type SyncedCollectionName,
} from './types';

/** K extends SyncedCollectionName guarantees DocOf<K> always has a
 * `syncedAt: number` field in its RxDB schema — TypeScript can't prove that
 * through the mapped-type indirection for a generic K, so this is the one
 * place that trust boundary is asserted, instead of scattered per call. */
type SyncedDoc = {
  syncedAt: number;
  status?: string;
  offlineSyncErrors?: unknown;
};

function assertSynced<K extends SyncedCollectionName>(
  col: RxCollection<DocOf<K>>
): RxCollection<SyncedDoc> {
  return col as unknown as RxCollection<SyncedDoc>;
}

export class RxDbBulkWriteError extends Error {
  constructor(
    public readonly collection: CollectionName,
    public readonly errors: unknown[]
  ) {
    super(`bulkUpsert on ${collection} had ${errors.length} write error(s)`);
    this.name = 'RxDbBulkWriteError';
  }
}

export class RxDbDataService {
  constructor(private readonly runner: RxDbActionRunner) {}

  async find<K extends CollectionName>(
    collection: K,
    query?: MangoQuery<DocOf<K>>
  ): Promise<DocOf<K>[]> {
    return this.runner.doAction(collection, async (col) => {
      const docs = await col.find(query).exec();
      return docs.map((d) => d.toMutableJSON());
    });
  }

  async findOne<K extends CollectionName>(
    collection: K,
    id: string
  ): Promise<DocOf<K> | null> {
    return this.runner.doAction(collection, async (col) => {
      const doc = await col.findOne(id).exec();
      return doc ? doc.toMutableJSON() : null;
    });
  }

  async count<K extends CollectionName>(
    collection: K,
    query?: MangoQuerySelectorAndIndex<DocOf<K>>
  ): Promise<number> {
    return this.runner.doAction(collection, (col) => col.count(query).exec());
  }

  async bulkUpsert<K extends CollectionName>(
    collection: K,
    docs: DocOf<K>[]
  ): Promise<DocOf<K>[]> {
    return this.runner.doAction(collection, async (col) => {
      const res = await col.bulkUpsert(docs);
      if (res.error.length) {
        throw new RxDbBulkWriteError(collection, res.error);
      }
      return res.success.map((d) => d.toMutableJSON());
    });
  }

  async remove<K extends CollectionName>(
    collection: K,
    id: string
  ): Promise<void> {
    await this.runner.doAction(collection, async (col) => {
      const doc = await col.findOne(id).exec();
      await doc?.remove();
    });
  }

  observeChanges<K extends CollectionName>(
    collection: K
  ): Observable<DocOf<K>> {
    return new Observable<DocOf<K>>((subscriber) => {
      let innerSub: { unsubscribe(): void } | undefined;
      let cancelled = false;
      this.runner
        .doAction(collection, async (col) => {
          if (cancelled) {
            return;
          }
          innerSub = col.$.subscribe((event) => {
            if (event.documentData) {
              subscriber.next(event.documentData);
            }
          });
        })
        .catch((err) => subscriber.error(err));
      return () => {
        cancelled = true;
        innerSub?.unsubscribe();
      };
    });
  }

  resolveCollectionForReplication<K extends CollectionName>(
    collection: K
  ): Promise<RxCollection<DocOf<K>>> {
    return this.runner.doAction(collection, async (col) => col);
  }

  async countAll(
    names: CollectionName[]
  ): Promise<{ name: CollectionName; count: number }[]> {
    return Promise.all(
      names.map((name) => this.count(name).then((count) => ({ name, count })))
    );
  }

  async insertSynced<K extends SyncedCollectionName>(
    collection: K,
    item: Omit<DocOf<K>, 'syncedAt'>,
    syncedAt?: number
  ): Promise<DocOf<K>> {
    return this.runner.doAction(collection, (col) =>
      insertSynced(assertSynced<K>(col), item, syncedAt)
    ) as unknown as Promise<DocOf<K>>;
  }

  async upsertSynced<K extends SyncedCollectionName>(
    collection: K,
    item: Omit<DocOf<K>, 'syncedAt'>,
    syncedAt?: number
  ): Promise<DocOf<K> & { syncedAt: number }> {
    return this.runner.doAction(collection, (col) =>
      upsertSynced(assertSynced<K>(col), item, syncedAt)
    ) as unknown as Promise<DocOf<K> & { syncedAt: number }>;
  }

  async bulkInsertSynced<K extends SyncedCollectionName>(
    collection: K,
    docs: Omit<DocOf<K>, 'syncedAt'>[],
    syncedAt?: number
  ): Promise<(DocOf<K> & { syncedAt: number })[]> {
    return this.runner.doAction(collection, (col) =>
      bulkInsertSynced(assertSynced<K>(col), docs, syncedAt)
    ) as unknown as Promise<(DocOf<K> & { syncedAt: number })[]>;
  }

  async bulkUpsertSynced<K extends SyncedCollectionName>(
    collection: K,
    docs: Omit<DocOf<K>, 'syncedAt'>[],
    syncedAt?: number
  ): Promise<(DocOf<K> & { syncedAt: number })[]> {
    return this.runner.doAction(collection, (col) =>
      bulkUpsertSynced(assertSynced<K>(col), docs, syncedAt)
    ) as unknown as Promise<(DocOf<K> & { syncedAt: number })[]>;
  }

  async patchSynced<K extends SyncedCollectionName>(
    collection: K,
    id: string,
    item: Partial<Omit<DocOf<K>, 'syncedAt'>>,
    syncedAt?: number
  ): Promise<DocOf<K> & { syncedAt: number }> {
    return this.runner.doAction(collection, (col) =>
      patchSynced(assertSynced<K>(col), id, item, syncedAt)
    ) as unknown as Promise<DocOf<K> & { syncedAt: number }>;
  }

  async softDeleteSynced<K extends SyncedCollectionName>(
    collection: K,
    id: string,
    syncedAt?: number
  ): Promise<DocOf<K> & { syncedAt: number }> {
    return this.runner.doAction(collection, (col) =>
      patchSynced(
        assertSynced<K>(col),
        id,
        { status: 'DELETED', offlineSyncErrors: null },
        syncedAt
      )
    ) as unknown as Promise<DocOf<K> & { syncedAt: number }>;
  }

  async removeStale<K extends CollectionName>(
    collection: K,
    primaryKey: string,
    ninIds: string[],
    scope?: MangoQuerySelector<DocOf<K>>
  ): Promise<DocOf<K>[]> {
    if (!ninIds.length) {
      return [];
    }

    const handleConflictError = (item: DocOf<K>) => (error: unknown) => {
      if (error instanceof RxError && error.code === 'CONFLICT') {
        return item;
      }
      throw error;
    };

    return this.runner.doAction(collection, async (col) =>
      col
        .find(scope ? { selector: scope } : {})
        .where(primaryKey)
        .nin([...new Set(ninIds)])
        .exec()
        .then((docs) =>
          Promise.all(
            docs.map((d) =>
              d
                .getLatest()
                .remove()
                .catch(handleConflictError(d.toMutableJSON()))
            )
          )
        )
    );
  }
}

export const createRxDbDataService = (
  runner: RxDbActionRunner
): RxDbDataService => new RxDbDataService(runner);
